import type { MemberGroupDefinition } from "../api/memberGroupDefinitions";
import { memberGroupsFromMemberRow } from "../api/memberGroupDefinitions";
import type { MemberLevelBrief } from "../api/memberLevels";
import type { MemberRow } from "../api/types";

export const ALL_ACTIVITY_LEVELS = '所有等级'
export const ALL_ACTIVITY_GROUPS = '所有分组'
/** 会员端展示用 */
export const ALL_ACTIVITY_LEVELS_LABEL = '所有会员等级'
export const ALL_ACTIVITY_GROUPS_LABEL = '所有会员分组'
/** @deprecated 兼容旧数据 */
export const LEGACY_ALL_MEMBERS = '全体会员'

export type ActivityTargetAudience = {
  levels: string[]
  groups: string[]
}

const DEFAULT_AUDIENCE: ActivityTargetAudience = {
  levels: [ALL_ACTIVITY_LEVELS],
  groups: [ALL_ACTIVITY_GROUPS],
}

function isAllLevelsToken(s: string): boolean {
  const t = String(s || '').trim()
  return t === ALL_ACTIVITY_LEVELS || t === LEGACY_ALL_MEMBERS
}

function isAllGroupsToken(s: string): boolean {
  return String(s || '').trim() === ALL_ACTIVITY_GROUPS
}

function cleanTokens(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((s) => String(s ?? '').trim())
    .filter((s) => s && s !== '新会员')
}

/** 解析 API / 表单下发的 targetAudience（对象或旧 string[]） */
export function parseActivityTargetAudience(raw: unknown): ActivityTargetAudience {
  if (raw == null) return { ...DEFAULT_AUDIENCE }

  if (Array.isArray(raw)) {
    const legacy = cleanTokens(raw)
    if (legacy.length === 0) return { ...DEFAULT_AUDIENCE }
    if (legacy.some(isAllLevelsToken)) return { ...DEFAULT_AUDIENCE }
    return normalizeActivityTargetAudience({ levels: legacy, groups: [ALL_ACTIVITY_GROUPS] })
  }

  if (typeof raw === 'object') {
    const o = raw as { levels?: unknown; groups?: unknown }
    return normalizeActivityTargetAudience({
      levels: cleanTokens(o.levels),
      groups: cleanTokens(o.groups),
    })
  }

  return { ...DEFAULT_AUDIENCE }
}

/** 规范化：互斥「所有*」与具体项；空则回默认 */
export function normalizeActivityTargetAudience(aud: ActivityTargetAudience): ActivityTargetAudience {
  let levels = cleanTokens(aud.levels)
  let groups = cleanTokens(aud.groups)

  if (levels.length === 0) levels = [ALL_ACTIVITY_LEVELS]
  else if (levels.some(isAllLevelsToken)) levels = [ALL_ACTIVITY_LEVELS]

  if (groups.length === 0) groups = [ALL_ACTIVITY_GROUPS]
  else if (groups.some(isAllGroupsToken)) groups = [ALL_ACTIVITY_GROUPS]

  return { levels, groups }
}

export function targetAudienceToApiBody(aud: ActivityTargetAudience): ActivityTargetAudience {
  return normalizeActivityTargetAudience(aud)
}

function resolveLevelLabel(keyOrName: string, levels?: MemberLevelBrief[]): string {
  const k = String(keyOrName || '').trim()
  if (!k || isAllLevelsToken(k)) return ALL_ACTIVITY_LEVELS
  const hit = levels?.find((l) => l.id === k || l.name === k)
  return (hit?.name || k).trim() || k
}

function resolveGroupLabel(key: string, groups?: MemberGroupDefinition[]): string {
  const k = String(key || '').trim()
  if (!k || isAllGroupsToken(k)) return ALL_ACTIVITY_GROUPS
  const hit = groups?.find((g) => g.id === k || g.name === k)
  return (hit?.name || k).trim() || k
}

function resolveLevelPart(normalized: ActivityTargetAudience, levelDefs?: MemberLevelBrief[]): string {
  if (normalized.levels.some(isAllLevelsToken)) return ALL_ACTIVITY_LEVELS_LABEL
  const names = normalized.levels.map((x) => resolveLevelLabel(x, levelDefs)).filter(Boolean)
  return names.length ? `${names.join('、')}等级` : ALL_ACTIVITY_LEVELS_LABEL
}

function resolveGroupPart(normalized: ActivityTargetAudience, groupDefs?: MemberGroupDefinition[]): string {
  if (normalized.groups.some(isAllGroupsToken)) return ALL_ACTIVITY_GROUPS_LABEL
  const names = normalized.groups.map((x) => resolveGroupLabel(x, groupDefs)).filter(Boolean)
  return names.length ? `${names.join('、')}分组` : ALL_ACTIVITY_GROUPS_LABEL
}

/** 会员端完整说明：活动参与对象：…范围内的会员 */
export function formatActivityTargetAudienceDescription(
  aud: ActivityTargetAudience | unknown,
  levelDefs?: MemberLevelBrief[],
  groupDefs?: MemberGroupDefinition[],
): string {
  const normalized = parseActivityTargetAudience(aud)
  const levelPart = resolveLevelPart(normalized, levelDefs)
  const groupPart = resolveGroupPart(normalized, groupDefs)
  return `活动参与对象：${levelPart}、${groupPart}范围内的会员`
}

/** 展示标签数组：如 ['所有会员等级', '所有会员分组']（简短标签，列表角标等） */
export function formatActivityTargetAudienceTags(
  aud: ActivityTargetAudience | unknown,
  levelDefs?: MemberLevelBrief[],
  groupDefs?: MemberGroupDefinition[],
): string[] {
  const normalized = parseActivityTargetAudience(aud)
  const levelTags = normalized.levels.map((x) =>
    isAllLevelsToken(x) ? ALL_ACTIVITY_LEVELS_LABEL : resolveLevelLabel(x, levelDefs),
  )
  const groupTags = normalized.groups.map((x) =>
    isAllGroupsToken(x) ? ALL_ACTIVITY_GROUPS_LABEL : resolveGroupLabel(x, groupDefs),
  )
  return [...levelTags, ...groupTags]
}

/** 列表/卡片一行文案（与完整说明一致） */
export function formatActivityTargetAudienceLabel(
  aud: ActivityTargetAudience | unknown,
  levelDefs?: MemberLevelBrief[],
  groupDefs?: MemberGroupDefinition[],
): string {
  return formatActivityTargetAudienceDescription(aud, levelDefs, groupDefs)
}

function memberLevelKeys(member: MemberRow): string[] {
  const keys = new Set<string>()
  for (const raw of [member.levelRankKey, member.levelRankName, member.level]) {
    const t = String(raw || '').trim()
    if (t) keys.add(t)
  }
  return [...keys]
}

function memberGroupKeys(member: MemberRow): string[] {
  const keys = new Set<string>()
  for (const g of memberGroupsFromMemberRow(member)) {
    const k = String(g.memberGroupKey || '').trim()
    const n = String(g.memberGroupName || '').trim()
    if (k) keys.add(k)
    if (n) keys.add(n)
  }
  return [...keys]
}

export type ActivityTargetAudienceMatchResult = {
  ok: boolean
  reason?: string
}

/** 会员是否符合活动目标对象（等级 AND 分组；组内多选为 OR） */
export function memberMatchesActivityTargetAudience(
  aud: ActivityTargetAudience | unknown,
  member: MemberRow | null | undefined,
  levelDefs?: MemberLevelBrief[],
  groupDefs?: MemberGroupDefinition[],
): ActivityTargetAudienceMatchResult {
  if (!member) return { ok: false, reason: '请先登录会员身份' }
  const normalized = parseActivityTargetAudience(aud)

  const levelOk =
    normalized.levels.some(isAllLevelsToken) ||
    normalized.levels.some((req) => {
      const keys = memberLevelKeys(member)
      return keys.some((k) => k === req || resolveLevelLabel(k, levelDefs) === resolveLevelLabel(req, levelDefs))
    })

  if (!levelOk) {
    return { ok: false, reason: '您的会员等级不符合本场活动的参与对象要求' }
  }

  const groupOk =
    normalized.groups.some(isAllGroupsToken) ||
    normalized.groups.some((req) =>
      memberGroupKeys(member).some(
        (k) => k === req || resolveGroupLabel(k, groupDefs) === resolveGroupLabel(req, groupDefs),
      ),
    )

  if (!groupOk) {
    return { ok: false, reason: '您的会员分组不符合本场活动的参与对象要求' }
  }

  return { ok: true }
}

/** 当前会员不在参与对象范围内时的提示（符合或未登录返回 null） */
export function activityTargetAudienceIneligibleHint(
  aud: ActivityTargetAudience | unknown,
  member: MemberRow | null | undefined,
  levelDefs?: MemberLevelBrief[],
  groupDefs?: MemberGroupDefinition[],
): string | null {
  if (!member) return null
  const match = memberMatchesActivityTargetAudience(aud, member, levelDefs, groupDefs)
  if (match.ok) return null
  return match.reason || '您不在本场活动的参与对象范围内，无法报名'
}

export function toggleActivityTargetLevel(
  current: ActivityTargetAudience,
  opt: string,
): ActivityTargetAudience {
  const normalized = normalizeActivityTargetAudience(current)
  if (isAllLevelsToken(opt)) {
    return { ...normalized, levels: [ALL_ACTIVITY_LEVELS] }
  }
  const base = normalized.levels.filter((x) => !isAllLevelsToken(x))
  const has = base.includes(opt)
  const next = has ? base.filter((x) => x !== opt) : [...base, opt]
  return normalizeActivityTargetAudience({
    ...normalized,
    levels: next.length ? next : [ALL_ACTIVITY_LEVELS],
  })
}

export function toggleActivityTargetGroup(
  current: ActivityTargetAudience,
  opt: string,
): ActivityTargetAudience {
  const normalized = normalizeActivityTargetAudience(current)
  if (isAllGroupsToken(opt)) {
    return { ...normalized, groups: [ALL_ACTIVITY_GROUPS] }
  }
  const base = normalized.groups.filter((x) => !isAllGroupsToken(x))
  const has = base.includes(opt)
  const next = has ? base.filter((x) => x !== opt) : [...base, opt]
  return normalizeActivityTargetAudience({
    ...normalized,
    groups: next.length ? next : [ALL_ACTIVITY_GROUPS],
  })
}
